/* @flow */
import {
  getterFromKeyPath
} from 'trustar/utils';
import {
  createSelector
} from 'reselect';
import {
  Map,
  Record,
  OrderedMap,
  Set,
  List
} from 'immutable';
import DATE_CONSTANTS from 'trustar-2.0/utils/Date.constants';

import {
  enclavesMap
} from 'modules/enclave/getters';
import {
  metadataCache
} from 'modules/reports/getters';
import {
  metadata as entityMetadata
} from 'modules/indicators/getters';
import {allTagOptionsEntities, allTagOptionsReports} from '../tags/getters';

export const searchTerm = getterFromKeyPath(['search', 'query']);
export const viewPanel = getterFromKeyPath(['search', 'panel']);
export const searchOptions = getterFromKeyPath(['search', 'options']);
export const iocsResponse = getterFromKeyPath(['search', 'iocs']);
export const reportsResponse = getterFromKeyPath(['search', 'reports']);

const iocsLoading = getterFromKeyPath(['search', 'iocs', 'loading']);
const reportsLoading = getterFromKeyPath(['search', 'reports', 'loading']);

/* Search 2.0 */
export const selectedSort = getterFromKeyPath(['search', 'selectedSort']);
export const defaultValues = {
  searchSorterPlaceholder: 'Select',
  searchSorterSearch: Map({
    text: 'Newest to Oldest Submitted',
    sort: {
      field: 'created',
      order: 'desc', // asc / desc
    }
  }),
};

/* END Search 2.0 */

export const iocsData = createSelector(
  [iocsResponse],
  (iocsResponse: Map<any, any>) => {
    return iocsResponse ? iocsResponse.get('data') : Map();
  }
);

export const searchLoading = createSelector(
  [iocsLoading, reportsLoading],
  (iocsLoading: boolean, reportsLoading: boolean): boolean => {
    return iocsLoading && reportsLoading;
  }
);

/* Determine if search returned zero results. If either query is still loading, return false */
export const noSearchResults = createSelector(
  [iocsResponse, reportsResponse],
  (iocs: Record<any>, reports: Record<any>): boolean => {
    if (iocs.get('loading') || reports.get('loading')) return false;
    if (iocs.get('totalPageElements') === 0 && reports.get('totalPageElements') === 0) {
      return true;
    }
    return false;
  }
);

/* Merge reports search results with reports metadata */
export const reportsWithMetadata = createSelector(
  [reportsResponse, metadataCache, enclavesMap],
  (reports, metadata, enclaves) => {
    return reports.update('data', d => {
      return d.map(r => {
        const meta = metadata.get(r.get('id'), Map());
        return r
          .merge(meta) // Merge metadata with data, but give data precedence
          .update('enclaves', e => e.map(enclave => enclaves.get(enclave.get('id'))));
      });
    });
  }
);

/* Total result tallies for search query */
export const searchResultsCount = createSelector(
  [iocsResponse],
  (iocs: Map<string, any>) => {
    return Map({
      iocs: iocs.get('totalPageElements'),
      reports: 0,
    });
  }
);

const IOCS_PREVIEW_LIMIT = 3;
const REPORTS_PREVIEW_LIMIT = 3;

/* Derive search results for presentation, filtered by view panel selection,
 * panel = ALL | IOCS | REPORTS
 */
export const searchResults = createSelector(
  [iocsResponse, reportsWithMetadata, viewPanel],
  (iocs: Map<string, any>, reports: Map<string, any>, panel: string) => {

    const iocsResult = Map({
      data: iocs,
      title: 'IOCs',
      category: 'iocs'
    });
    const reportsResult = Map({
      data: reports,
      title: 'Incident Reports',
      category: 'incidentReports'
    });

    const iocsPreview = iocsResult
      .set('data', iocs
        .update('data', d => d.take(IOCS_PREVIEW_LIMIT)));

    const reportsPreview = reportsResult
      .set('data', reports
        .update('data', d => d.take(REPORTS_PREVIEW_LIMIT)));

    switch (panel) {
      case 'ALL':
        return Map({
          iocs: iocsPreview,
          incidentReports: reportsPreview
        });
      case 'IOCS':
        return Map({
          iocs: iocsResult
        });
      case 'REPORTS':
        return Map({
          incidentReports: reportsResult
        });
      default:
        console.warn(`viewPanel for search results is an unexpected value: ${panel}`);
        return Map();
    }
  }
);

// When on pages that apply tags to both reports and entities at the same time, we must make sure only the relevant
// tags are selected for each category
const removeNonReportTags = (payload: Map<string, any>, store) => {
  const currentState = store.getState();
  const entityTags = allTagOptionsEntities(currentState);
  const reportTagsInSearchPayload = payload.get('tagFilters');
  const onlyReportTags = Map({
    included: reportTagsInSearchPayload.get('included').subtract(entityTags.included),
    excluded: reportTagsInSearchPayload.get('excluded').subtract(entityTags.excluded)
  });

  return payload.set('tagFilters', onlyReportTags);
};
const removeNonEntityTags = (payload: Map<string, any>, store) => {
  const currentState = store.getState();
  const reportTags = allTagOptionsReports(currentState);
  const entityTagsInSearchPayload = payload.get('tagFilters');
  const onlyEntityTags = Map({
    included: entityTagsInSearchPayload.get('included').subtract(reportTags.included),
    excluded: entityTagsInSearchPayload.get('excluded').subtract(reportTags.excluded)
  });

  return payload.set('tagFilters', onlyEntityTags);
};

// Search 2.0
export const currentViewPanel = getterFromKeyPath(['search', 'currentViewPanel']);

export const reportPageQueryRaw = getterFromKeyPath(['search', 'reportPageQueryRaw']);
export const queryResults = getterFromKeyPath(['search', 'searchResults']);
export const reportsLoadingAllData = getterFromKeyPath(['search', 'reportsLoadingAllData']);

/* Reports without irrelevant tags */
export const reportPageQuery = createSelector(
  [reportPageQueryRaw, allTagOptionsEntities],
  (reportQueryObject, allEntityTags) => {
    const reportTagsInSearchPayload = reportQueryObject.get('tagFilters');
    const onlyReportTags = Map({
      included: reportTagsInSearchPayload.get('included').subtract(allEntityTags.included),
      excluded: reportTagsInSearchPayload.get('excluded').subtract(allEntityTags.excluded)
    });
    return reportQueryObject.set('tagFilters', onlyReportTags);
  });


/* Merge reports search results with reports metadata */
export const reportResultsWithMeta = createSelector(
  [queryResults, metadataCache, enclavesMap],
  (queryResults, metadata, enclaves) => {
    return queryResults.get('reports').update('items', report => {
      return report.map(r => {
        const meta = metadata.get(r.get('guid'), Map());
        const reportEnclave = enclaves.get(r.get('enclaveGuid'));
        return r
          .merge(meta) // Merge metadata with data, but give data precedence
          .set('enclaves', List([reportEnclave]))
          .update('enclaves', enclaves => Set(enclaves))
          .set('title', r.get('title')); // Reset title to ensure metadata doesn't override potentially new title
      });
    });
  }
);

export const entityPageQueryRaw = getterFromKeyPath(['search', 'entityPageQueryRaw']);
export const entitiesLoadingAllData = getterFromKeyPath(['search', 'entitiesLoadingAllData']);

/* Reports without irrelevant tags */
export const entityPageQuery = createSelector(
  [entityPageQueryRaw, allTagOptionsReports],
  (entityQueryObject, allReportTags) => {
    const reportTagsInSearchPayload = entityQueryObject.get('tagFilters');
    const onlyEntityTags = Map({
      included: reportTagsInSearchPayload.get('included').subtract(allReportTags.included),
      excluded: reportTagsInSearchPayload.get('excluded').subtract(allReportTags.excluded)
    });
    return entityQueryObject.set('tagFilters', onlyEntityTags);
  });

/* Merge entities search results with metadata */
export const entityResultsWithMeta = createSelector(
  [queryResults, entityMetadata, enclavesMap],
  (queryResults, metadata, enclaves) => {
    return queryResults.get('entities').update('items', entity => {
      return entity.map(r => {
        let meta = metadata.get(r.get('guid'), Map());
        // We want to use non-meta dates for first and last seen because that's how our sort works
        meta = meta.set('lastSeen', r.get('lastSeen'));

        return r
          .merge(meta) // Merge metadata with data, but give data precedence
          .set('enclaves', r.get('enclaveGuids').map(enclave => enclaves.get(enclave)))
          .update('enclaves', enclaves => Set(enclaves));
      });
    });
  }
);

// Search page will have its own query object, but the actions it provides will map out to reports and iocs
export const searchPageQuery = getterFromKeyPath(['search', 'reportPageQueryRaw']);
export const searchPayloadHasBeenInitialized = getterFromKeyPath(['search', 'searchPayloadHasBeenInitialized']);

export const allSearchResultsWithMeta = createSelector(
  [reportResultsWithMeta, entityResultsWithMeta],
  (reportResults, entityResults) => {
    const reportsTotal = reportResults.get('totalElements', 0);
    const entitiesTotal = entityResults.get('totalElements', 0);

    return Map({
      reportResults,
      entityResults,
      reportsTotal,
      entitiesTotal,
      totalResults: reportsTotal + entitiesTotal,
    });
  }
);

export const defaultFilters = {
  number: Map({
    type: 'NUMBER',
    text: 'Greater Than',
    operator: 'gt',
    firstValue: 0,
  }),
  boolean: Map({
    type: 'BOOLEAN',
    text: 'Select Caption',
    firstValue: false,
  }),
  date: Map({
    type: 'DATE',
    text: 'Date',
    operator: 'RANGE',
    firstValue: DATE_CONSTANTS.LAST_MONTH,
    secondValue: DATE_CONSTANTS.NOW,
  }),
  tag: Map({
    included: Set(),
    excluded: Set()
  }),
  term: Map({
    type: 'TERMS',
    values: Set()
  }),
};

export const defaultSorterValues = {
  searchSorterPlaceholder: 'Select',
  searchSorterSearchReports: Map({
    text: 'Newest to Oldest Updated',
    sort: {
      sortBy: 'updated',
      sortDirection: 'DESC', // ASC / DESC
    }
  }),
  searchSorterSearchEntities: Map({
    text: 'Most Recent Last Seen',
    sort: {
      sortBy: 'lastSeen',
      sortDirection: 'DESC'
    }
  })
};

export const entityTypeOptions = Set(
  ['DOMAIN', 'URL', 'SOFTWARE', 'MALWARE', 'CVE', 'SHA256', 'SHA1', 'MD5', 'EMAIL_ADDRESS', 'REGISTRY_KEY', 'IP', 'BITCOIN_ADDRESS', 'CIDR_BLOCK', 'PHONE_NUMBER', 'X_ID', 'THREAT_ACTOR']
);

export const reportSortOptions = [
  {
    text: 'Newest to Oldest Updated',
    sort: {
      sortBy: 'updated',
      sortDirection: 'DESC'
    }
  },
  {
    text: 'Oldest to Newest Updated',
    sort: {
      sortBy: 'updated',
      sortDirection: 'ASC'
    }
  },
  {
    text: 'Newest to Oldest Submitted',
    sort: {
      sortBy: 'created',
      sortDirection: 'DESC'
    }
  },
  {
    text: 'Oldest to Newest Submitted',
    sort: {
      sortBy: 'created',
      sortDirection: 'ASC'
    }
  }
];

export const entitySortOptions = [{
  text: 'Most Recent Last Seen',
  sort: {
    sortBy: 'lastSeen',
    sortDirection: 'DESC'
  }
},
  {
    text: 'Least Recent Last Seen',
    sort: {
      sortBy: 'lastSeen',
      sortDirection: 'ASC'
    }
  },
  {
    text: 'Most Recent First Seen',
    sort: {
      sortBy: 'firstSeen',
      sortDirection: 'DESC'
    }
  },
  {
    text: 'Least Recent First Seen',
    sort: {
      sortBy: 'firstSeen',
      sortDirection: 'ASC'
    }
  },
];


export const defaultReportsSearchPayload =
  Map({
    query: '',
    filters: OrderedMap({}),
    sort: defaultSorterValues.searchSorterSearchReports,
    enclaveFilters: Set([]),
    tagFilters: defaultFilters.tag,
    pageNumber: 0,
    pageSize: 25,
  });

export const defaultEntitiesSearchPayload =
  Map({
    query: '',
    filters: OrderedMap({}),
    sort: defaultSorterValues.searchSorterSearchEntities,
    enclaveFilters: Set([]),
    tagFilters: defaultFilters.tag,
    pageNumber: 0,
    pageSize: 25,
  });

export const defaultReportResult = Map({
  empty: true,
  items: List(), // PAGE SIZE and stuff? TODO
});

export const defaultEntityResult = Map({
  empty: true,
  items: List(), // PAGE SIZE and stuff? TODO
});

export const defaultSearchResults = Map({
  reports: defaultReportResult,
  entities: defaultEntityResult
});



// WEBPACK FOOTER //
// ./src/app/modules/search/getters.js